/* Deep indigo brand bar (ui-redesign §5.2): white text and controls on --topbar-bg. */
.bar {
  display: flex;
  align-items: center;
  gap: 12px;
  height: var(--topbar-height);
  flex: none;
  /* The indigo band runs edge to edge; past 1920px the bar's content stays aligned with the
     centred, 1920px-capped content area (ui-redesign §4.2, 2560 row). */
  padding: 0 max(var(--gutter), calc((100% - var(--content-max)) / 2 + var(--gutter)));
  background: var(--topbar-bg);
  color: var(--white);
}

/* Focus inside the bar: a white ring (the accent ring would vanish on the indigo). */
.bar :focus-visible {
  outline: 2px solid var(--white);
  outline-offset: 1px;
}

.spacer {
  flex: 1;
}

.segmented {
  display: inline-flex;
  padding: 3px;
  background: color-mix(in srgb, var(--white) 14%, transparent);
  border-radius: 10px;
}

.segment {
  display: inline-flex;
  align-items: center;
  padding: 5px 14px;
  border-radius: var(--radius-sm);
  color: var(--topbar-muted);
  text-decoration: none;
  white-space: nowrap;
}

.segment:hover {
  color: var(--white);
}

/* The selected view: a white chip with accent text (§5.2 分段控件). */
.segmentActive {
  background: var(--surface);
  color: var(--accent-700);
  box-shadow: var(--shadow-sm);
}

.segmentActive:hover {
  color: var(--accent-700);
}

.dateNav {
  display: flex;
  align-items: center;
  gap: 6px;
}

.dateLabel {
  min-width: 96px;
  text-align: center;
  white-space: nowrap;
}

.todayButton {
  height: var(--control-h-sm);
  padding: 0 12px;
  background: transparent;
  border: 1px solid color-mix(in srgb, var(--white) 50%, transparent);
  border-radius: var(--radius-sm);
  color: var(--white);
}

.todayButton:hover:not(:disabled) {
  background: color-mix(in srgb, var(--white) 16%, transparent);
}

.todayButton:disabled {
  color: var(--topbar-muted);
  border-color: color-mix(in srgb, var(--white) 25%, transparent);
  background: transparent;
}

.group {
  display: flex;
  gap: 2px;
}

.iconButton {
  display: inline-grid;
  place-items: center;
  width: var(--control-h-sm);
  height: var(--control-h-sm);
  padding: 0;
  background: transparent;
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  color: var(--white);
  text-decoration: none;
}

.iconButton:hover:not(:disabled) {
  background: color-mix(in srgb, var(--white) 16%, transparent);
}

.iconButton:disabled {
  color: var(--topbar-muted);
  opacity: 0.45;
  background: transparent;
}

.dayStats {
  display: flex;
  gap: 12px;
  margin: 0;
  font-size: var(--font-size-sm);
  color: color-mix(in srgb, var(--white) 90%, transparent);
  white-space: nowrap;
}

/* Narrow end of the two-column range (768-919px; a bar-density choice independent of the column
   breakpoint, kept at 919 by ui-iteration-2026-10b §7.2): the bar cannot hold the totals line
   next to the view switcher and date navigation without overflowing the window. */
@media (max-width: 919px) {
  .dayStats {
    display: none;
  }

  /* One more icon (action history) fits between 768 and 919px only with a tighter gap. */
  .bar {
    gap: 8px;
  }
}

.dayStats > span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

/* Category dot of the stats line: 2px white ring, or it drowns in the indigo (§5.2). */
.statDot {
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  box-shadow: 0 0 0 2px var(--white);
}

.pending {
  color: var(--white);
  font-weight: 600;
}

@media (max-width: 767px) {
  /* Two-row bar: only the first row keeps the indigo; the date row below is white (§5.2). */
  .bar {
    flex-wrap: wrap;
    height: auto;
    min-height: var(--topbar-height);
    padding: 8px var(--gutter) 0;
    row-gap: 0;
  }

  .spacer {
    display: none;
  }

  .dateNav {
    order: 3;
    flex: 1 0 100%;
    justify-content: space-between;
    margin: 8px calc(-1 * var(--gutter)) 0;
    padding: 6px var(--gutter);
    background: var(--surface);
    border-bottom: 1px solid var(--border);
    color: var(--text);
  }

  /* Second-row controls go back to the light theme. */
  .dateNav .iconButton {
    border-color: var(--border);
    background: var(--surface);
    color: var(--text);
  }

  .dateNav .iconButton:hover:not(:disabled) {
    background: var(--surface-muted);
  }

  .dateNav .iconButton:disabled {
    color: var(--text-muted);
  }

  .dateNav .todayButton {
    border-color: var(--border);
    background: var(--surface);
    color: var(--text);
  }

  .dateNav .todayButton:hover:not(:disabled) {
    background: var(--surface-muted);
  }

  .dateNav .todayButton:disabled {
    color: var(--text-muted);
    border-color: var(--border);
    background: transparent;
  }

  .dateNav :focus-visible {
    outline-color: var(--accent);
  }

  /* Four segments stay within 176px at 360px (2026-10b §9.1): each at least 40px wide. */
  .segment {
    justify-content: center;
    min-width: 40px;
    padding: 5px 10px;
  }
}

/* Touch: every control of the bar is 44px high; the bar grows by the difference only. */
@media (max-width: 767px) and (pointer: coarse) {
  .bar {
    padding-block: 4px 0;
    row-gap: 0;
    /* Five 44px icon controls (undo, redo, sync, help, settings) share the first row with the
       view switcher at 390px: no gap keeps it to one row (the 44px boxes already space the icons) (2026-10c §8). */
    column-gap: 0;
  }

  .helpButton {
    margin-left: 0;
  }

  .dateNav {
    margin-top: 4px;
    padding-block: 4px;
  }

  .segmented {
    padding: 0;
    border-radius: var(--radius-sm);
  }

  .segment {
    justify-content: center;
    min-width: 44px;
    min-height: 44px;
    padding-inline: 8px;
  }

  .exportHint {
    display: inline-flex;
    align-items: center;
    min-height: 44px;
  }
}

/* ---- sync status and backup hint ---- */
.syncLink {
  display: inline-grid;
  place-items: center;
  width: var(--control-h-sm);
  height: var(--control-h-sm);
  border-radius: var(--radius-sm);
}

/* With a note beside the dot (email not verified) the link grows to fit it. */
.syncLinkNote {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: auto;
  padding: 0 10px;
  color: var(--white);
  text-decoration: none;
}

.syncNote {
  font-size: var(--font-size-sm);
  white-space: nowrap;
}

.syncLink:hover {
  background: color-mix(in srgb, var(--white) 16%, transparent);
}

/* Status dots carry a 2px white ring: without it they sit at 1.5–1.9:1 on the indigo and a
   sync failure would go unseen (§5.2 状态点). */
.syncDot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--text-muted);
  box-shadow: 0 0 0 2px var(--white);
}

.syncIdle {
  background: var(--success);
}

.syncSyncing {
  background: var(--accent);
  animation: syncPulse 1.2s ease-in-out infinite;
}

.syncOffline {
  background: var(--text-muted);
}

.syncError {
  background: var(--danger);
}

/* The cloud data is gone: a danger ring on a white disc, distinct from a transient error. */
.syncSpaceMissing {
  background: var(--white);
  box-shadow:
    0 0 0 2px var(--white),
    inset 0 0 0 2px var(--danger);
}

@keyframes syncPulse {
  50% {
    opacity: 0.35;
  }
}

@media (prefers-reduced-motion: reduce) {
  .syncSyncing {
    animation: none;
  }
}

.exportHint {
  padding: 2px 10px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--white) 16%, transparent);
  color: var(--white);
  font-size: var(--font-size-sm);
  text-decoration: none;
  white-space: nowrap;
}

.exportHint:hover {
  background: color-mix(in srgb, var(--white) 24%, transparent);
}

/* The "?" help button (2026-10c §8): 8px after the sync indicator (the bar gap is 12px), 44px on
   coarse pointers. Below 360px the bar cannot hold it next to the gear, so it is hidden there. */
.helpButton {
  margin-left: -4px;
  font-size: var(--font-size-sm);
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
}

@media (pointer: coarse) {
  .helpButton {
    width: 44px;
    height: 44px;
  }
}

@media (max-width: 359px) {
  .helpButton {
    display: none;
  }
}

/* Action history: a button with a read-only list panel below it, right-aligned to the button. */
.historyRoot {
  position: relative;
}

.historyPanel {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 70;
  box-sizing: border-box;
  width: 380px;
  max-width: calc(100vw - 16px);
  max-height: min(70vh, 560px);
  padding: 12px;
  overflow-y: auto;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  background: var(--surface);
  box-shadow: var(--shadow-pop);
  color: var(--text);
}

.historyTitle {
  margin: 0;
  font-size: var(--font-size);
  font-weight: 600;
}

.historyHint,
.historyEmpty {
  margin: 4px 0 8px;
  color: var(--text-muted);
  font-size: var(--font-size-sm);
}

.historyList {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.historyList li {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 4px 6px;
  border-radius: var(--radius-xs);
}

.historyList li[data-undone='true'] {
  color: var(--text-muted);
}

.historyList li[data-undone='true'] .historyText {
  text-decoration: line-through;
  text-decoration-thickness: 1px;
}

.historyTime {
  flex: none;
  color: var(--text-muted);
  font-size: var(--font-size-sm);
  font-variant-numeric: tabular-nums;
}

.historyText {
  flex: 1 1 auto;
  min-width: 0;
  overflow-wrap: anywhere;
}

.historyUndone {
  flex: none;
  font-size: var(--font-size-sm);
}
